import { ParsedUrlQuery } from "querystring";

import { MY_LOAD_LORRY_TABS } from "@vahak/core/dist/constants/tab";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

export const DEEP_LINK_REDIRECT = {
    LOAD_ADD: "load|add",
    LOAD_DETAILS: "load|details",
    LORRY_REACTIVATE: "lorry|reactivate"
} as const;

export type DeepLinkAction = (typeof DEEP_LINK_REDIRECT)[keyof typeof DEEP_LINK_REDIRECT];

/** Canonical query param for web deep links. */
export const DEEP_LINK_QUERY_PARAM = "action";

/** Legacy query param — still present on older Firebase / page.link URLs. Load-add only. */
export const DEEP_LINK_QUERY_PARAM_LEGACY = "r";

/** Load details entity id. Same key the listing page uses to open `Details`. */
export const DEEP_LINK_ID_PARAM = "id";

/** Lorry reactivate entity id. Not `id` — listing `id` opens details instead of the reactivate modal. */
export const DEEP_LINK_LORRY_ID_PARAM = "lorryid";

export function getRedirectQueryValue(query: ParsedUrlQuery, key: string): string | undefined {
    const value = query[key];
    return Array.isArray(value) ? value[0] : value;
}

function getQueryString(asPath?: string): string {
    return asPath?.split("?")[1]?.split("#")[0] ?? "";
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function hasPipeActionInQuery(query: ParsedUrlQuery, key: string, action: string): boolean {
    return getRedirectQueryValue(query, key) === action;
}

function hasPipeActionInAsPath(asPath: string | undefined, key: string, action: string): boolean {
    const [left, right] = action.split("|");
    if (!left || !right) {
        return false;
    }

    // Unencoded `|` is parsed as `key=left` only — fall back to raw URL.
    return new RegExp(
        `(?:^|&)${escapeRegex(key)}=${escapeRegex(left)}(?:%7C|\\|)${escapeRegex(right)}(?:&|$)`,
        "i"
    ).test(getQueryString(asPath));
}

function hasPipeAction(query: ParsedUrlQuery, asPath: string | undefined, action: string, keys: string[]): boolean {
    return keys.some(
        (key) => hasPipeActionInQuery(query, key, action) || hasPipeActionInAsPath(asPath, key, action)
    );
}

export function isLoadAddDeepLink(query: ParsedUrlQuery, asPath?: string): boolean {
    return hasPipeAction(query, asPath, DEEP_LINK_REDIRECT.LOAD_ADD, [
        DEEP_LINK_QUERY_PARAM,
        DEEP_LINK_QUERY_PARAM_LEGACY
    ]);
}

export function isLoadDetailsDeepLink(query: ParsedUrlQuery, asPath?: string): boolean {
    return hasPipeAction(query, asPath, DEEP_LINK_REDIRECT.LOAD_DETAILS, [DEEP_LINK_QUERY_PARAM]);
}

export function isLorryReactivateDeepLink(query: ParsedUrlQuery, asPath?: string): boolean {
    return hasPipeAction(query, asPath, DEEP_LINK_REDIRECT.LORRY_REACTIVATE, [DEEP_LINK_QUERY_PARAM]);
}

export function isAuthRequiredDeepLink(query: ParsedUrlQuery, asPath?: string): boolean {
    return isLoadDetailsDeepLink(query, asPath) || isLorryReactivateDeepLink(query, asPath);
}

function pathWithoutQuery(pathname: string, asPath: string): string {
    return asPath.split("?")[0].split("#")[0] || pathname;
}

export function isMyLoadsListingPath(pathname: string, asPath: string): boolean {
    const path = pathWithoutQuery(pathname, asPath);
    return /\/ac\/my-loads\/listing\/?$/.test(path) || /\/ac\/my-loads\/listing\/?$/.test(pathname);
}

export function isMyLorriesListingPath(pathname: string, asPath: string): boolean {
    const path = pathWithoutQuery(pathname, asPath);
    return /\/ac\/my-lorries\/listing\/?$/.test(path) || /\/ac\/my-lorries\/listing\/?$/.test(pathname);
}

function appendQuery(path: string, params: Record<string, string>): string {
    const search = Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

    return search ? `${path}?${search}` : path;
}

/**
 * Logged-in destination after landing on `/` (auth guard + post-login).
 * Load-add stays on `/dashboard` so compact posting can open. The other two
 * go to the listing page that already owns that UI.
 */
export function getLoggedInDeepLinkHref(query: ParsedUrlQuery, asPath?: string): string | undefined {
    if (isLoadAddDeepLink(query, asPath)) {
        return appendQuery(ROUTES.dashboard.getUrl(), {
            [DEEP_LINK_QUERY_PARAM]:
                getRedirectQueryValue(query, DEEP_LINK_QUERY_PARAM) ??
                getRedirectQueryValue(query, DEEP_LINK_QUERY_PARAM_LEGACY) ??
                DEEP_LINK_REDIRECT.LOAD_ADD
        });
    }

    if (isLoadDetailsDeepLink(query, asPath)) {
        const loadId = getRedirectQueryValue(query, DEEP_LINK_ID_PARAM);
        const listing = ROUTES.myLoadsMyLorries.getUrl("loads", MY_LOAD_LORRY_TABS.LISTING);
        return loadId ? appendQuery(listing, { [DEEP_LINK_ID_PARAM]: loadId }) : listing;
    }

    if (isLorryReactivateDeepLink(query, asPath)) {
        const lorryId = getRedirectQueryValue(query, DEEP_LINK_LORRY_ID_PARAM);
        const listing = ROUTES.myLoadsMyLorries.getUrl("lorries", MY_LOAD_LORRY_TABS.LISTING);
        return appendQuery(listing, {
            [DEEP_LINK_QUERY_PARAM]: DEEP_LINK_REDIRECT.LORRY_REACTIVATE,
            ...(lorryId ? { [DEEP_LINK_LORRY_ID_PARAM]: lorryId } : {})
        });
    }

    return undefined;
}
